import { useState, memo } from 'react';

import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import FormControl from '@zenbusiness/zui/FormControl';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import TextField from '@zenbusiness/zui/TextField';
import PropTypes from 'prop-types';
import { v1 as uuidv1 } from 'uuid';

import AutoCompleteInput from '#client/components/AutoCompleteInput';
import {
  TRANSACTION_VOLUME_OPTIONS,
  TRANSACTION_VOLUMES_REQUIRING_DESCRIPTION,
  UNIT_TEXT_DETAIL_MAX_LENGTH
} from '#client/constants/additionalInfo';
import addToString from '#client/utils/addToString';

import { StyledPopper } from './components/IndustryUIFieldVerticalList.styles';

import { isOptionEqualToValue, renderOption } from './utils/verticalListOptions';

const options = addToString(TRANSACTION_VOLUME_OPTIONS);

const TransactionVolumeUIField = ({
  field = { setSaved: () => {}, setLoading: () => {} },
  dataChangeHandler = () => {},
  disabled,
  data
}) => {
  const [selectedOption, setSelectedOption] = useState(
    options.find((option) => option.value === data?.transactionVolume)
  );
  const [descriptionValue, setDescriptionValue] = useState(
    data?.transactionVolumeDescription || ''
  );
  // Gate validation errors on user interaction so in-progress applications don't show red on load.
  const [hasInteracted, setHasInteracted] = useState(false);

  const trackTransactionVolumeChangeClick = useButtonPressedCallback({
    elementName: 'banking_collect_info',
    appPageName: 'banking_application'
  });

  const isDescriptionRequired = TRANSACTION_VOLUMES_REQUIRING_DESCRIPTION.includes(
    selectedOption?.value
  );
  const showDescriptionError = hasInteracted && isDescriptionRequired && !descriptionValue.trim();

  // currentSelectedOption override avoids stale closure when called right after setSelectedOption.
  const onDataChanged = (event, option, currentSelectedOption = selectedOption) => {
    const enumValue = currentSelectedOption?.value;
    if (option || enumValue) {
      trackTransactionVolumeChangeClick(event);
      field.setLoading();
    }
    // Per Unit V2, transactionVolumeDescription is required when the enum is $1M+.
    // Gate field.setSaved on that completeness check so the submit button stays
    // disabled until the description is provided for triggering enum values.
    const trimmedDescription = (option ?? descriptionValue).trim();
    const isComplete =
      Boolean(enumValue) &&
      (!TRANSACTION_VOLUMES_REQUIRING_DESCRIPTION.includes(enumValue) ||
        Boolean(trimmedDescription));
    dataChangeHandler(
      !!option || !!enumValue,
      {
        transactionVolume: enumValue,
        transactionVolumeDescription: trimmedDescription || null
      },
      (forceUpdate, { isSave, isError }) => {
        field.setSaved(isSave && isComplete, isError);
        forceUpdate();
      },
      field.key
    );
  };

  const handleChangeOption = (event, option) => {
    setSelectedOption(option);
    // Mark interacted when a triggering enum is picked so the error shows without waiting for blur.
    if (option?.value && TRANSACTION_VOLUMES_REQUIRING_DESCRIPTION.includes(option.value)) {
      setHasInteracted(true);
    }
    onDataChanged(event, undefined, option);
  };

  const handleTransactionVolumeDescriptionChange = (event) => {
    setDescriptionValue(event.target.value);
  };

  const handleTransactionVolumeDescriptionBlur = (event) => {
    setHasInteracted(true);
    onDataChanged(event, event.target.value);
  };

  return (
    <FormControl
      fullWidth
      sx={{ margin: 0, display: 'flex', flexDirection: 'column', gap: 3 }}
      variant="outlined"
    >
      <AutoCompleteInput
        autoHighlight
        blurOnSelect
        disabled={disabled}
        getOptionLabel={(option) => option.toString()}
        isOptionEqualToValue={isOptionEqualToValue}
        ListboxProps={{
          style: {
            maxHeight: '400px',
            py: 2,
            borderRadius: 2
          }
        }}
        noOptionsText="No results. Try a new keyword."
        onChange={handleChangeOption}
        openOnFocus
        options={options}
        PopperComponent={StyledPopper}
        renderInput={(params) => (
          <TextField
            {...params}
            inputProps={{
              ...params.inputProps,
              // Use for preventing auto suggestion from browser
              name: uuidv1()
            }}
            // eslint-disable-next-line react/jsx-no-duplicate-props
            InputProps={{
              ...params.InputProps
            }}
            label="Select"
          />
        )}
        renderOption={renderOption}
        value={selectedOption || null}
      />

      <Stack spacing={1.5}>
        <Text
          color="tertiary"
          size="md"
        >
          Please explain more detail around transaction volume selection.
        </Text>

        <TextField
          disabled={disabled}
          error={showDescriptionError}
          helperText={
            showDescriptionError ? 'Details are required for this transaction volume.' : ''
          }
          id="transactionVolumeDescription"
          inputProps={{
            maxLength: UNIT_TEXT_DETAIL_MAX_LENGTH
          }}
          label="Enter details"
          onBlur={handleTransactionVolumeDescriptionBlur}
          onChange={handleTransactionVolumeDescriptionChange}
          type="text"
          value={descriptionValue}
        />
      </Stack>
    </FormControl>
  );
};

TransactionVolumeUIField.propTypes = {
  data: PropTypes.shape({
    transactionVolume: PropTypes.string,
    transactionVolumeDescription: PropTypes.string
  }),
  dataChangeHandler: PropTypes.func,
  field: PropTypes.shape({
    setSaved: PropTypes.func,
    setLoading: PropTypes.func,
    key: PropTypes.string
  }),
  disabled: PropTypes.bool,
  useFor: PropTypes.string
};

function areEqual(preProp, nextProp) {
  return (
    preProp.field === nextProp.field &&
    preProp.data === nextProp.data &&
    preProp.disabled === nextProp.disabled
  );
}

export default memo(TransactionVolumeUIField, areEqual);
